<template>
  <div>
    <div
      v-if="assessableApplications.length"
      class="border-b-2 mb-6 pb-12"
    >
      <h2 class="text-xl mb-2">
        Submitted Applications ({{ assessableApplications.length }}/{{
          applications.length
        }})
      </h2>
      <div class="flex">
        <icon-link
          href="#"
          icon="add-circle"
          class="mr-4"
          @click.prevent="addApplicationAssessorsAll()"
        >
          Assign All Assessors
        </icon-link>

        <icon-link
          href="#"
          icon="remove-circle"
          class="mr-4"
          @click.prevent="removeApplicationAssessorsAll()"
        >
          Remove All Assessors
        </icon-link>

        <icon-link
          slot="reference"
          href="#"
          icon="check"
          type="positive"
          @click.prevent="sendAssessorNotifications"
        >
          Send Notification
        </icon-link>
      </div>
      <ul class="mt-6 list-reset">
        <li
          v-for="(lot, index) in assessableApplicationsGrouped"
          :key="index"
          :class="{
            'mb-12': groupByCount > 1
          }"
        >
          <template v-if="groupByCount > 1">
            <h2 class="text-sm border-b pb-2 mb-2 uppercase tracking-wide">
              Lot {{ index + 1 }}
            </h2>
          </template>
          <ul class="list-reset flex flex-wrap">
            <li
              v-for="(application, index) in lot"
              :key="application.uuid"
              class="w-full pb-4 flex"
              :class="{
                'border-b mb-4': index + 1 !== assessableApplications.length
              }"
            >
              <div class="flex-1 mr-4">
                <h3 class="text-base">
                  {{ applicationTitle(application) }}
                </h3>
                <p class="text-grey-700 text-sm">
                  {{ getApplicant(application.uuid, "company") | sentencecase }}
                </p>
              </div>
              <div class="flex justify-end">
                <ul
                  v-if="getApplicationAssessors(application.uuid).length"
                  class="flex list-reset"
                >
                  <li
                    v-for="(assessor, ai) in getApplicationAssessors(
                      application.uuid
                    )"
                    :key="index + '_' + ai"
                  >
                    <popper
                      :options="{
                        placement: 'bottom',
                        'delay-on-mouse-over': 0,
                        'delay-on-mouse-out': 0,
                        modifiers: {
                          arrow: {
                            element: '.arrow'
                          },
                          offset: {
                            offset: '0,8px'
                          }
                        }
                      }"
                    >
                      <div
                        class="relative bg-black px-4 py-2 text-center text-white z-100 max-w-xs"
                      >
                        <div class="arrow text-black" />
                        <p class="text-xs">
                          {{ assessor.name }}
                        </p>
                        <p
                          v-if="assessor.company"
                          class="text-2xs text-grey-200"
                        >
                          {{ assessor.company.name | sentencecase }}
                        </p>
                      </div>
                      <button
                        slot="reference"
                        type="button"
                        class="focus:outline-none w-12 h-12 relative bg-primary-600 flex justify-center items-center text-white text-xs ml-1 font-bold cursor-pointer hover:bg-negative-400 transition-all transition-normal rounded-full"
                        @click="
                          removeApplicationAssessor(
                            application.uuid,
                            assessor.id
                          )
                        "
                      >
                        {{
                          assessor.first_name[0] + (assessor.last_name[0] || "")
                        }}
                        <span
                          class="absolute pin-b pin-r -mr-1 -mb-1 border-2 border-white rounded-full"
                        >
                          <icon
                            icon="close-circle"
                            class="w-4 h-4 icons-negative block -m-px"
                          />
                        </span>
                      </button>
                    </popper>
                  </li>
                </ul>
                <button
                  type="button"
                  class="focus:outline-none hover:bg-primary-300 bg-primary-200 transition-all transition-normal block ml-1 w-12 h-12 flex items-center justify-center rounded-full"
                  @click="addAssessorOpen(application.uuid)"
                >
                  <icon
                    icon="add"
                    class="w-8 h-8 icons-primary block"
                  />
                  <span class="hidden">Assign Assessor</span>
                </button>
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <div v-if="draftApplications.length">
      <h2 class="text-xl mb-1">
        {{ applicationOpen ? "Draft" : "Expired" }} Applications ({{
          draftApplications.length
        }}/{{ applications.length }})
      </h2>
      <p class="text-grey-700 italic">
        You can only assign assessors to submitted applications.
      </p>
      <ul class="mt-6 list-reset flex flex-wrap">
        <li
          v-for="(application, index) in draftApplications"
          :key="application.uuid"
          class="w-full pb-4 flex"
          :class="{
            'border-b mb-4': index + 1 !== draftApplications.length
          }"
        >
          <div class="flex-1 mr-4">
            <h3 class="text-base">
              {{ applicationTitle(application) }}
            </h3>
            <p class="text-grey-700 text-sm">
              {{ getApplicant(application.uuid, "company") | sentencecase }}
            </p>
          </div>
        </li>
      </ul>
    </div>
    <portal to="modals">
      <app-modal
        v-if="addAssessor.show"
        @close="addAssessorClose"
      >
        <div class="w-full">
          <app-card class="is-borderless max-w-sm mx-auto overflow-hidden p-4">
            <h2 class="text-lg mb-2">
              {{ applicationTitle(addAssessor.applicationUUID) }} assessors
            </h2>
            <icon-link
              :href="
                route('funding.competitions.show', {
                  funding: state.competition.funding_pots[0].id,
                  competition: state.competition.uuid
                }) + '#assessors'
              "
              icon="user-circle"
            >
              Add new assessor
            </icon-link>
            <ul
              v-if="assessors.length"
              class="list-reset mb-4 border-t-2 mt-4"
            >
              <li
                v-for="(assessor, index) in assessors"
                :key="index"
                class="flex justify-between items-center py-3 px-2"
                :class="{
                  'border-b': index !== assessors.length - 1
                }"
              >
                <div class="mr-2">
                  <p>{{ assessor.name }}</p>
                  <p
                    v-if="assessor.company !== null && assessor.company.name"
                    class="text-grey-700"
                  >
                    <small>{{ assessor.company.name | sentencecase }}</small>
                  </p>
                </div>
                <button
                  v-if="
                    addAssessorAssigned(
                      addAssessor.applicationUUID,
                      assessor.id
                    )
                  "
                  type="button"
                  class="bg-negative-100 uppercase text-xs px-3 py-1 font-bold text-negative-800 focus:outline-none hover:bg-negative-200 focus:bg-negative-200 transition-all transition-normal"
                  @click="
                    removeApplicationAssessor(
                      addAssessor.applicationUUID,
                      assessor.id
                    )
                  "
                >
                  Remove
                </button>
                <button
                  v-else
                  type="button"
                  class="bg-primary-100 uppercase text-xs px-3 py-1 font-bold text-primary-800 focus:outline-none hover:bg-primary-200 focus:bg-primary-200 transition-all transition-normal"
                  @click="
                    addApplicationAssessor(
                      addAssessor.applicationUUID,
                      assessor.id
                    )
                  "
                >
                  Add
                </button>
              </li>
            </ul>
            <div class="md:w-48 md:ml-auto">
              <app-button @click="addAssessorClose">
                Close
              </app-button>
            </div>
          </app-card>
        </div>
      </app-modal>
    </portal>
  </div>
</template>

<script>
import Popper from "vue-popperjs";
import { debounce, chunk } from "lodash";
import { competitionApplicationsMixin } from "../../mixins/competition";
export default {
  name: "CompetitionApplicastionAssessors",
  components: {
    Popper
  },
  mixins: [competitionApplicationsMixin],
  props: {
    state: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      applicationAssessors: [],
      addAssessor: {
        show: false,
        applicationUUID: null
      },
      groupByCount: 1
    };
  },
  computed: {
    assessableApplicationsGrouped() {
      return this.groupByCount && this.groupByCount > 1
        ? chunk(this.assessableApplications, this.groupByCount)
        : chunk(this.assessableApplications, 1);
    }
  },
  watch: {
    applicationAssessors: {
      deep: true,
      handler(newAssessors) {
        const { uuid, funding_pots } = this.state.competition;
        return this.saveApplicationAssessors(
          {
            funding: funding_pots[0].id,
            competition: uuid
          },
          [...newAssessors]
        );
      }
    }
  },
  methods: {
    // Application Assessors Methods
    saveApplicationAssessors: debounce(function(params, assessors) {
      return axios
        .post(route("funding.competitions.applications", params), [
          ...assessors
        ])
        .then(({ data }) => console.log("Response:", data));
    }, 500),

    getApplicationIndex(uuid) {
      return this.applicationAssessors.findIndex(
        application => application.uuid === uuid
      );
    },

    getApplicationAssessors(uuid) {
      let aa = this.applicationAssessors.find(application => {
        return application.uuid === uuid;
      });
      if (!aa) return [];
      console.log(
        this.getAssessors([...aa.assessors]).sort((a, b) =>
          a.name > b.name ? 1 : -1
        )
      );
      return this.getAssessors([...aa.assessors]).sort((a, b) =>
        a.name > b.name ? 1 : -1
      );
    },
    removeApplicationAssessor(uuid, assessor_id) {
      if (
        prompt(
          "Would you like to this assessor? WARNING: This will remove any grading that this assessor has completed for this application. Please type DELETE to confirm."
        ) !== "DELETE"
      )
        return;
      const applicationIndex = this.getApplicationIndex(uuid);
      if (applicationIndex > -1) {
        const assessors = this.applicationAssessors[applicationIndex].assessors;
        this.applicationAssessors[applicationIndex].assessors = [
          ...assessors.filter(id => id !== assessor_id)
        ];
      }
    },
    addApplicationAssessor(uuid, assessor_id) {
      const applicationIndex = this.getApplicationIndex(uuid);
      if (applicationIndex > -1) {
        return this.applicationAssessors[applicationIndex].assessors.push(
          assessor_id
        );
      } else {
        return this.applicationAssessors.push({
          uuid,
          assessors: [assessor_id]
        });
      }
    },
    addApplicationAssessorsAll() {
      if (
        !confirm(
          "Would you like to add all the assessors to every application?"
        )
      )
        return;
      const allAssessors = this.getAssessorsAllIDs();
      this.assessableApplications.map(application => {
        const applicationIndex = this.getApplicationIndex(application.uuid);
        if (applicationIndex > -1) {
          return (this.applicationAssessors[
            applicationIndex
          ].assessors = allAssessors);
        } else {
          return this.applicationAssessors.push({
            uuid: application.uuid,
            assessors: allAssessors
          });
        }
      });
    },
    removeApplicationAssessorsAll() {
      if (
        prompt(
          "Would you like to remove all the assessors from all applications? WARNING: This will remove any grading that the assessors have undertaken. Please type DELETE to confirm."
        ) !== "DELETE"
      )
        return;
      return this.applicationAssessors.map(
        application => (application.assessors = [])
      );
    },

    // Add Assessor Modal Methods
    addAssessorOpen(uuid) {
      this.addAssessor.show = true;
      this.addAssessor.applicationUUID = uuid;
    },
    addAssessorClose() {
      this.addAssessor.show = false;
      this.addAssessor.applicationUUID = null;
    },
    addAssessorAssigned(uuid, assessor_id) {
      const applicationIndex = this.getApplicationIndex(uuid);
      return applicationIndex > -1
        ? this.applicationAssessors[applicationIndex].assessors.includes(
            assessor_id
          )
        : false;
    },

    // Assessor Methods Methods
    getAssessorsAllIDs() {
      return this.assessors.reduce((arr, assessor) => {
        arr.push(assessor.id);
        return arr;
      }, []);
    },
    getAssessors(assessors) {
      return assessors.reduce((arr, assessor) => {
        arr.push(this.getAssessor(assessor));
        return arr;
      }, []);
    },
    getAssessor(id) {
      const a = this.assessors.find(assessor => assessor.id === id);
      return a || false;
    },

    sendAssessorNotifications() {
      return axios
        .post(
          route("funding.competitions.send-assessor-notifications", {
            funding: this.state.competition.funding_pots[0].id,
            competition: this.state.competition.uuid
          })
        )
        .then(() =>
          alert("Notifications have been sent to assigned assessors")
        );
    },

    populateApplicationAssessorsFromState() {
      if (this.state.applications.length > 0) {
        this.state.applications.forEach(application => {
          if (application.assessors.length > 0) {
            let applicationUUID = application.uuid;
            let applicationAssessors = [];
            application.assessors.forEach(assessor => {
              applicationAssessors.push(assessor.user_id);
            });
            this.applicationAssessors.push({
              uuid: applicationUUID,
              assessors: applicationAssessors
            });
          }
        });
      }
    }
  },
  created() {
    this.populateApplicationAssessorsFromState();
    this.groupByCount = this.state.competition.application_assessor_lot_size;
  }
};
</script>
